import { FC, useRef } from "react";
import { Asset, Entry } from "contentful";
import CTA, { CTAProps } from "../CTA";
import ArrowIcon from "../Icons/CircleArrow";
import { useIntersection } from "../../hooks/useIntersection";
import { 
  RootContainer, 
  ComponentContainer, 
  DesktopAssetContainer, 
  MobileAssetContainer, 
  Video, 
  Image, 
  StyledCTAWrapper, 
  EyebrowContainer, 
  ArrowWrapper 
} from "./styles";
import { getCloudflareImageUrl } from "../../lib/getCloudflareImageUrl";

interface MediaTileProps extends Entry {
  id: string;
  brand: string;
  fields: {
    assetAlignment: string;
    cta?: CTAProps;
    desktopAsset: Asset;
    marginBottom?: number;
    marginTop?: number;
    maxWidth?: number;
    mobileAsset?: Asset;
    style?: string;
    theme?: string;
    eyebrow?: string;
  };
  parentProps: {
    isContentInGrid?: boolean;
    parentComponentId: string;
  };
}

const RenderedAsset = (asset: Asset, inView: boolean) => {
  const contentType = asset.fields.file?.contentType as string;

  if (contentType.includes("video")) {
    return (
      <Video autoPlay loop muted playsInline>
        <source src={`https:${asset.fields.file?.url}`} type={contentType} />
      </Video>
    );
  }

  const imageSrc = getCloudflareImageUrl(`https:${asset.fields.file?.url}`);
  return <Image loading={inView ? "eager" : "lazy"}  src={imageSrc} />;
};

const MediaTile: FC<MediaTileProps> = ({
  id,
  brand,
  fields: {
    assetAlignment,
    cta,
    desktopAsset,
    marginBottom,
    marginTop,
    maxWidth,
    mobileAsset,
    style = "Default",
    theme,
    eyebrow,
  },
  parentProps: { 
    parentComponentId,
    isContentInGrid = false
  }, 
}) => {
  const CTARef = useRef<HTMLAnchorElement>(null);

  const handleTileClick = () => {
    if (cta && CTARef && CTARef.current) {
      CTARef.current.click();
    }
  };

  const desktopRef = useRef<HTMLDivElement>(null);
  const desktopInView = useIntersection(desktopRef);

  const mobileRef = useRef<HTMLDivElement>(null);
  const mobileInView = useIntersection(mobileRef);

  return (
    <RootContainer
      $alignment={assetAlignment}
      $cursor={cta ? "pointer" : "initial"}
      $isContentInGrid={isContentInGrid}
      data-component-id={id}
      onClick={() => handleTileClick()}
    >
      <ComponentContainer
        $marginBottom={marginBottom}
        $marginTop={marginTop}
        $maxWidth={maxWidth}
        $style={style}
        $theme={theme}
        $fullWidth={(parentComponentId === "contentGrid" || isContentInGrid)}
      >
        {eyebrow && (
          <EyebrowContainer $brand={brand} $theme={theme}>
            {eyebrow}
          </EyebrowContainer>
        )}

        <DesktopAssetContainer
          $isContentInGrid={isContentInGrid}
          $hideMobile={mobileAsset ? true : false}
          $fullHeight={parentComponentId === "contentGrid"}
        >
          {RenderedAsset(desktopAsset, desktopInView)}
        </DesktopAssetContainer>
        {mobileAsset && (
          <MobileAssetContainer>{RenderedAsset(mobileAsset, mobileInView)}</MobileAssetContainer>
        )}

        {cta && (
          <StyledCTAWrapper $brand={brand} $theme={theme}>
            <CTA {...cta} ref={CTARef} brand={brand} />
          </StyledCTAWrapper>
        )}

        {cta && brand == "fender" && (
          <ArrowWrapper>
            <ArrowIcon direction="right"/>
          </ArrowWrapper>
        )}
      </ComponentContainer>
    </RootContainer>
  );
};

export default MediaTile;
